/**
 * LiveContext — global SSE connection shared across the app.
 *
 * - Subscribes once to /api/events/stream at app boot (after login).
 * - Shows toast notifications for important events.
 * - Tracks currently-processing dossiers (live counter for sidebar/header).
 * - Exposes subscribe() so individual pages can react to events without
 *   creating their own EventSource connections.
 */
import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { useEventStream } from "./useEventStream";
import { useAuth } from "./auth";

const LiveContext = createContext(null);

const STATUS_TOAST = {
  COMPLET: { fn: toast.success, icon: "✅" },
  INCOMPLET: { fn: toast.warning, icon: "⚠️" },
  A_VERIFIER: { fn: toast.warning, icon: "👀" },
  ERREUR: { fn: toast.error, icon: "❌" },
};

export function LiveProvider({ children }) {
  const { user } = useAuth();
  const [processing, setProcessing] = useState({}); // id -> summary
  const listenersRef = useRef(new Set());

  const dispatch = useCallback((event) => {
    listenersRef.current.forEach((cb) => {
      try {
        cb(event);
      } catch {}
    });
  }, []);

  const handleEvent = useCallback(
    (event) => {
      dispatch(event);
      const { type, payload } = event;
      if (type === "dossier:started") {
        const label = payload.partenaire || payload.sender_email || "Manuel";
        toast.info(`📨 Nouveau dossier reçu`, {
          description: `${label} · Démarrage du traitement…`,
          id: `start-${payload.id}`,
          duration: 4000,
        });
        setProcessing((prev) => ({ ...prev, [payload.id]: payload }));
      } else if (type === "dossier:step") {
        setProcessing((prev) => ({ ...prev, [payload.id]: { ...prev[payload.id], ...payload } }));
      } else if (type === "dossier:completed") {
        const meta = STATUS_TOAST[payload.status] || { fn: toast, icon: "📂" };
        const name = `${payload.nom || "?"} ${payload.prenom || ""}`.trim() || "Dossier";
        meta.fn(`${meta.icon} ${name}`, {
          description: `Statut : ${payload.status?.replace("_", " ") || "?"} · ${payload.produit || ""}`,
          duration: 6000,
          action: payload.id
            ? {
                label: "Voir",
                onClick: () => {
                  window.location.href = `/dossiers/${payload.id}`;
                },
              }
            : undefined,
        });
        setProcessing((prev) => {
          const next = { ...prev };
          delete next[payload.id];
          return next;
        });
      }
      // NOTE: 'gmail:poll' events are intentionally silent — no toast.
      // Only dossier-level events surface in the UI.
    },
    [dispatch]
  );

  const { connected } = useEventStream({ onEvent: user ? handleEvent : undefined });

  const subscribe = useCallback((cb) => {
    listenersRef.current.add(cb);
    return () => listenersRef.current.delete(cb);
  }, []);

  const processingList = Object.values(processing);

  return (
    <LiveContext.Provider value={{ connected, processing: processingList, subscribe }}>
      {children}
    </LiveContext.Provider>
  );
}

export const useLive = () => useContext(LiveContext) || { connected: false, processing: [], subscribe: () => () => {} };
